import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Sidebar } from '@/components/layout/Sidebar';
import { Canvas } from '../components/Canvas';
import { NodePalette } from '../components/NodePalette';
import { NodeProperties } from '../components/NodeProperties';
import { EditorStatusBar } from '../components/EditorStatusBar';
import { PromptBar } from '../components/PromptBar';
import { IaCMenu } from '../components/IaCMenu';
import { IaCPanel } from '../components/IaCPanel';
import { OnboardingModal } from '../components/OnboardingModal';
import { EmptyCanvasPrompt } from '../components/EmptyCanvasPrompt';
import { EditorOverflowMenu } from '../components/EditorOverflowMenu';
import { CloudSaveButton } from '../components/CloudSaveButton';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAutosave, formatLastSaved } from '../hooks/useAutosave';
import { useEditorStore } from '../store';
import { useAuthStore } from '@/features/auth/store';
import { BlastRadiusProvider } from '../lib/blastRadiusContext';
import type { IaCFormat } from '@/types/iac';

function onboardingKey(userId: string | undefined): string | null {
  return userId ? `clyra-onboarded-${userId}` : null;
}

function EditorInner({ projectId }: { projectId?: string }) {
  useKeyboardShortcuts();
  const { projectName, setProjectName, saving, lastSaved } = useAutosave(projectId);
  // Owned here so both CloudSaveButton (writes it on save) and the
  // overflow menu (reads it to gate History/Share) share one source.
  const projectIdRef = useRef<string | null>(projectId ?? null);
  const [savedText, setSavedText] = useState('');
  const [showAI, setShowAI] = useState(true);
  const [iacOpen, setIacOpen] = useState(false);
  const [iacFormat, setIacFormat] = useState<IaCFormat>('terraform');
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const nodes = useEditorStore((s) => s.nodes);
  const addNode = useEditorStore((s) => s.addNode);
  const user = useAuthStore((s) => s.user);

  const handleIaCChoose = useCallback((format: IaCFormat) => {
    setIacFormat(format);
    setIacOpen(true);
  }, []);

  const dismissOnboarding = useCallback(() => {
    const key = onboardingKey(user?.id);
    if (key) localStorage.setItem(key, '1');
    setOnboardingOpen(false);
  }, [user?.id]);

  // Onboarding modal is superseded by the empty-canvas prompt card, which
  // gives a new user an immediate thing to do. Keep the modal code around
  // but don't auto-open it — `EmptyCanvasPrompt` is the first-run surface.


  // No clearCanvas on mount — the editor used to wipe the store whenever
  // there was no :projectId in the URL, which silently destroyed diagrams
  // pre-loaded by ProjectsPage (cloud open) and TemplateGallery. Now the
  // "fresh canvas" intent lives at the call sites: any button that means
  // "new diagram" calls useEditorStore.getState().clearCanvas() before
  // navigating here. Callers that pre-populate the store just navigate.

  const projectNameRef = useRef(projectName);
  projectNameRef.current = projectName;

  const handleProjectRename = useCallback(
    (name: string) => {
      if (projectNameRef.current === 'Untitled Diagram') {
        setProjectName(name);
      }
    },
    [setProjectName],
  );

  useEffect(() => {
    const tick = () => setSavedText(formatLastSaved(lastSaved));
    tick();
    const id = setInterval(tick, 10_000);
    return () => clearInterval(id);
  }, [lastSaved]);

  const handleAddGroup = useCallback(() => {
    const groupId = `group-${Date.now()}`;
    addNode({
      id: groupId,
      type: 'group',
      position: { x: 100, y: 100 },
      data: { label: 'New Group', nodeType: 'generic.container' },
      style: { width: 500, height: 350 },
    });
  }, [addNode]);

  const isEmpty = nodes.length === 0;

  return (
    <AppLayout
      topbarChildren={
        <>
          <input
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="w-28 shrink-0 truncate rounded-clyra border border-transparent bg-transparent px-1.5 py-0.5 text-petit text-ink-secondary outline-none hover:border-line-default focus:border-brand-500/50 focus:text-ink-primary transition-colors"
            title="Project name"
          />
          <CloudSaveButton
            projectName={projectName}
            localSavedText={savedText ? `Saved ${savedText}` : ''}
            saving={saving}
            projectIdRef={projectIdRef}
          />

          {/* AI toggle + prompt bar. `min-w-0` lets the prompt input
              actually shrink at mid widths instead of pushing the right
              cluster into overlap. */}
          <div className="ml-2 flex min-w-0 flex-1 items-center gap-1.5 max-w-xl">
            <button
              onClick={() => setShowAI((s) => !s)}
              className={`shrink-0 rounded-clyra px-2 py-1 text-micro font-medium transition-all ${
                showAI
                  ? 'bg-brand-500/10 text-brand-400'
                  : 'text-ink-faint hover:text-ink-secondary hover:bg-clyra-overlay/30'
              }`}
              title={showAI ? 'Hide AI prompt' : 'Show AI prompt'}
            >
              AI
            </button>
            {showAI && (
              <div className="min-w-0 flex-1">
                <PromptBar onProjectRename={handleProjectRename} />
              </div>
            )}
          </div>

          <IaCMenu onChoose={handleIaCChoose} />

          <EditorOverflowMenu
            projectIdRef={projectIdRef}
            projectName={projectName}
            onAddGroup={handleAddGroup}
          />
        </>
      }
    >
      <Sidebar side="left">
        <NodePalette />
      </Sidebar>

      <div className="flex flex-1 flex-col overflow-hidden relative">
        <Canvas />
        {isEmpty && <EmptyCanvasPrompt onProjectRename={handleProjectRename} />}
        <EditorStatusBar />
      </div>

      <Sidebar side="right" width="w-64">
        <NodeProperties />
      </Sidebar>

      <IaCPanel
        open={iacOpen}
        format={iacFormat}
        onClose={() => setIacOpen(false)}
        projectName={projectName}
      />

      <OnboardingModal open={onboardingOpen} onDismiss={dismissOnboarding} />
    </AppLayout>
  );
}

export function EditorPage() {
  const { projectId } = useParams<{ projectId: string }>();

  return (
    <ReactFlowProvider key={projectId ?? 'new'}>
      <BlastRadiusProvider>
        <EditorInner projectId={projectId} />
      </BlastRadiusProvider>
    </ReactFlowProvider>
  );
}
